import React, { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { cn } from '../../lib/utils';
import WorkflowStepEditorShell from './WorkflowStepEditorShell';

const EMAIL_SOURCE_OPTIONS = [
  { value: '{{trigger.email}}', label: '{{trigger.email}}' },
  { value: '{{contact.email}}', label: '{{contact.email}}' },
  { value: 'custom', label: 'Custom merge field' },
];

const LOOKUP_MODES = [
  { value: 'find_user_and_portal', label: 'Find user and resolve portal' },
  { value: 'find_user_only', label: 'Find user only' },
  { value: 'resolve_portal_only', label: 'Resolve portal only' },
];

const OUTPUT_FIELDS = [
  '{{user.id}}',
  '{{user.email}}',
  '{{user.first_name}}',
  '{{user.exists}}',
  '{{portal.name}}',
  '{{portal.host}}',
  '{{portal.reset_base_url}}',
];

const defaultConfig = () => ({
  action_name: 'Find User & Portal',
  email_source: '{{trigger.email}}',
  custom_email_source: '',
  lookup_mode: 'find_user_and_portal',
  normalize_email: true,
  silent_if_not_found: true,
  require_active_portal_user: true,
  respect_isolation: true,
});

const FindUserPortalEditor = ({
  isOpen,
  onClose,
  groupItem,
  onSave,
  advancedMode = false,
  width,
  overlayMode,
  onOverlayDismiss,
  activePanelType = 'find-user-portal',
  chromeHeight,
}) => {
  const [config, setConfig] = useState(defaultConfig());
  const [emailSourceMode, setEmailSourceMode] = useState('{{trigger.email}}');

  useEffect(() => {
    if (!isOpen || !groupItem) return;
    const incoming = { ...defaultConfig(), ...(groupItem.groupConfig || {}) };
    setConfig(incoming);
    const preset = EMAIL_SOURCE_OPTIONS.find((opt) => opt.value === incoming.email_source);
    setEmailSourceMode(preset ? incoming.email_source : 'custom');
  }, [isOpen, groupItem]);

  const resolvedEmailSource = useMemo(() => {
    if (emailSourceMode === 'custom') return config.custom_email_source || config.email_source;
    return emailSourceMode;
  }, [emailSourceMode, config.custom_email_source, config.email_source]);

  if (!isOpen) return null;

  const handleSave = () => {
    onSave?.({
      ...config,
      email_source: resolvedEmailSource,
    });
    onClose?.();
  };

  return (
    <WorkflowStepEditorShell
      isOpen={isOpen}
      width={width}
      overlayMode={overlayMode}
      onOverlayDismiss={onOverlayDismiss}
      activePanelType={activePanelType}
      chromeHeight={chromeHeight}
      className="border-l border-slate-200 bg-white"
      testId="find-user-portal-editor"
    >
      <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Find User & Portal</h2>
          <p className="text-xs text-slate-500">Data Tool for user lookup and portal resolution</p>
        </div>
        <button type="button" onClick={onClose} className="rounded p-2 text-slate-400 hover:bg-slate-100">
          <X size={18} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
        <div>
          <Label htmlFor="fup-action-name">Action Name</Label>
          <Input
            id="fup-action-name"
            value={config.action_name}
            onChange={(e) => setConfig((prev) => ({ ...prev, action_name: e.target.value }))}
            className="mt-1"
          />
        </div>

        <div>
          <Label>Email Source</Label>
          <div className="mt-2 space-y-2">
            {EMAIL_SOURCE_OPTIONS.map((option) => (
              <label key={option.value} className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="radio"
                  name="fup-email-source"
                  checked={emailSourceMode === option.value}
                  onChange={() => setEmailSourceMode(option.value)}
                />
                {option.label}
              </label>
            ))}
          </div>
          {emailSourceMode === 'custom' ? (
            <Input
              value={config.custom_email_source}
              onChange={(e) => setConfig((prev) => ({ ...prev, custom_email_source: e.target.value }))}
              className="mt-2"
              data-testid="fup-custom-email-source"
            />
          ) : null}
        </div>

        <div>
          <Label>Lookup Mode</Label>
          <div className="mt-2 space-y-2">
            {LOOKUP_MODES.map((mode) => (
              <label key={mode.value} className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="radio"
                  name="fup-lookup-mode"
                  checked={config.lookup_mode === mode.value}
                  onChange={() => setConfig((prev) => ({ ...prev, lookup_mode: mode.value }))}
                />
                {mode.label}
              </label>
            ))}
          </div>
        </div>

        <div>
          <Label>Safety Options</Label>
          <div className="mt-2 space-y-2 rounded-lg border border-slate-200 p-3">
            {[
              ['normalize_email', 'Normalize email'],
              ['silent_if_not_found', 'Silent if user not found'],
              ['require_active_portal_user', 'Require active portal user'],
              ['respect_isolation', 'Respect agency/platform isolation'],
            ].map(([key, label]) => (
              <label key={key} className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={Boolean(config[key])}
                  onChange={(e) => setConfig((prev) => ({ ...prev, [key]: e.target.checked }))}
                />
                {label}
              </label>
            ))}
          </div>
        </div>

        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
          <h3 className="text-sm font-semibold text-emerald-800">Output Variables</h3>
          <div className="mt-3 flex flex-wrap gap-2">
            {OUTPUT_FIELDS.map((field) => (
              <code key={field} className="rounded bg-white px-2 py-1 text-xs text-emerald-700 border border-emerald-100">
                {field}
              </code>
            ))}
          </div>
        </div>

        {advancedMode ? (
          <div className="rounded-lg border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-800">
            This Data Tool replaces the separate internal steps: find_user_by_email + resolve_user_portal
          </div>
        ) : null}
      </div>

      <div className="flex items-center justify-between border-t border-slate-200 bg-slate-50 px-5 py-4">
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={handleSave} data-testid="fup-save">
          Save action
        </Button>
      </div>
    </WorkflowStepEditorShell>
  );
};

export default FindUserPortalEditor;
